import {
    Box,
    Checkbox,
    FormControlLabel,
    FormLabel,
    Paper,
    TextField,
    Typography,
    Button,
} from "@mui/material";
import { useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useProductTypes } from "../../../services/productsService";
import { ProductStatusDropdown } from "./ProductStatusDropdown";
import { ProductTypeDropdown } from "./ProductTypeDropdown";
import { ProductStyleDropdown } from "./ProductStyleDropdown";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import { SendProposalDialog } from "../SendProposalDialog/SendProposalDialog";
import { PROUDCT_STATUS_FICTIONAL_RESERVATION_ID } from "../../../helpers/constants";

const disabledInputSx = {
    "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: "#010a1a",
    },
};

const visualDisabledInputSx = {
    "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: "#818181ff",
    },
};

const disabledCheckboxSx = {
    "& .MuiCheckbox-root.Mui-disabled": {
        color: "rgba(0, 0, 0, 0.6)",
    },
    "& .MuiFormControlLabel-label.Mui-disabled": {
        color: "rgba(0, 0, 0, 0.87)",
    },
};

export const ProductParameters = (props) => {
    const { control, forMultipleProducts, fieldNames, readOnly = false, hideTitle = false, isFinalSettlement = false, productId, productStatusId, investmnetHomeId} = props;

    const { t } = useTranslation();
    const { openDialog: openProposalDialog } = useDialogBetter();
    
      const handleOpenProposalDialog = (e) => {
        e.stopPropagation();
        e.preventDefault();
        openProposalDialog({
          Component: SendProposalDialog,
          componentProps: {
            productIds: [productId],
            disabledStatusProposal: productStatusId !== PROUDCT_STATUS_FICTIONAL_RESERVATION_ID,
          },
        });
      };

    const content = (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 2,
                    justifyContent: "space-between",
                }}
            >
                <FirstColumn
                    control={control}
                    forMultipleProducts={forMultipleProducts}
                    readOnly={readOnly}
                    fieldNames={fieldNames}
                    investmnetHomeId = {investmnetHomeId}
                />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: "200px" }}>
                    <SideCheckboxColumn
                        control={control}
                        fieldNames={fieldNames.sides}
                        disabled={forMultipleProducts || readOnly}
                    />
                    {readOnly && <Button variant="contained"  style={{width: "200px"}} onClick={handleOpenProposalDialog}>{t('products.ProductParameters.submitProposal')}</Button>}
                </Box>
                <FootageColumn
                    control={control}
                    forMultipleProducts={forMultipleProducts}
                    readOnly={readOnly || isFinalSettlement}
                    fieldNames={{
                        footage: fieldNames.footage,
                        footageFinal: fieldNames.footageFinal,
                        type: fieldNames.type,
                    }}
                />
            </Box>
        </>
    );

    if (hideTitle) {
        return <>{content}</>;
    }

    return (
        <Paper
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                margin: "1rem",
                padding: "1rem",
            }}
        >
            <Typography variant="h3" align="left" sx={{ width: "100%", fontSize: "1.5rem" }}>
                {t("products.ProductParameters.title")}
            </Typography>
            {content}
        </Paper>
    );
};

const FirstColumn = (props) => {
    const { t } = useTranslation();
    const { control, forMultipleProducts, readOnly, fieldNames, investmnetHomeId  } = props;
    const formLabelSx = { minWidth: "120px", textAlign: "left", color: "primary.main" };
    const formControlBoxSx = { display: "flex", alignItems: "center", gap: 2, minWidth: "400px" };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
            <Box sx={formControlBoxSx}>
                <FormLabel htmlFor="name" sx={formLabelSx}>
                    {t("products.ProductParameters.name")}
                </FormLabel>
                <Controller
                    name={fieldNames.name}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            id="name"
                            fullWidth
                            disabled={forMultipleProducts || readOnly}
                            sx={
                                readOnly
                                    ? disabledInputSx
                                    : forMultipleProducts
                                      ? undefined
                                      : disabledInputSx
                            }
                        />
                    )}
                />
            </Box>
            <Box sx={formControlBoxSx}>
                <FormLabel htmlFor="type" sx={formLabelSx}>
                    {t("products.ProductParameters.type")}{forMultipleProducts ? "" : "*"}
                </FormLabel>
                <ProductTypeDropdown
                    control={control}
                    fieldName={fieldNames.type}
                    disabled={readOnly}
                />
            </Box>
            <Box sx={formControlBoxSx}>
                <FormLabel htmlFor="productNumber" sx={formLabelSx}>
                    {t("products.ProductParameters.productNumber")}
                </FormLabel>
                <Controller
                    name={fieldNames.productNumber}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            id="productNumber"
                            fullWidth
                            disabled={forMultipleProducts || readOnly}
                            sx={
                                readOnly
                                    ? disabledInputSx
                                    : forMultipleProducts
                                      ? undefined
                                      : disabledInputSx
                            }
                        />
                    )}
                />
            </Box>
            <Box sx={formControlBoxSx}>
                <FormLabel htmlFor="num_rooms" sx={formLabelSx}>
                    {t("products.ProductParameters.nRooms")}
                </FormLabel>
                <Controller
                    name={fieldNames.num_rooms}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            id="num_rooms"
                            type="number"
                            fullWidth
                            disabled={readOnly}
                            inputProps={{
                                min: 1,
                                step: "1",
                            }}
                            sx={
                                readOnly
                                    ? disabledInputSx
                                    : undefined
                            }
                        />
                    )}
                />
            </Box>
            <Box sx={formControlBoxSx}>
                <FormLabel htmlFor="floor" sx={formLabelSx}>
                    {t("products.ProductParameters.floor")}
                </FormLabel>
                <Controller
                    name={fieldNames.floor}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            id="floor"
                            type="number"
                            fullWidth
                            disabled={readOnly}
                            inputProps={{
                                step: "1",
                            }}
                            sx={
                                readOnly
                                    ? disabledInputSx
                                    : undefined
                            }
                        />
                    )}
                />
            </Box>
            <Box sx={formControlBoxSx}>
                <FormLabel htmlFor="status" sx={formLabelSx}>
                    {t("products.ProductParameters.status")}{forMultipleProducts ? "" : "*"}
                </FormLabel>
                <ProductStatusDropdown
                    control={control}
                    fieldName={fieldNames.status}
                    disabled={readOnly}
                    forMultipleProducts={forMultipleProducts}
                />
            </Box>
            {!forMultipleProducts && <Box sx={formControlBoxSx}>
                <FormLabel htmlFor="style" sx={formLabelSx}>
                    {t("products.ProductParameters.style")}
                </FormLabel>
                <ProductStyleDropdown
                    investment={investmnetHomeId}
                    control={control}
                    fieldName={fieldNames.style}
                    disabled={readOnly || forMultipleProducts}
                    forMultipleProducts={forMultipleProducts}
                />
            </Box>}
        </Box>
    );
};

const SideCheckboxColumn = (props) => {
    const { t } = useTranslation();
    const { control, fieldNames, disabled = false } = props;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: "200px" }}>
            <FormLabel sx={{ textAlign: "left", fontWeight: "bold", color: "primary.main" }}>
                {t("products.ProductParameters.sides")}
            </FormLabel>
            <Controller
                name={fieldNames.east}
                control={control}
                render={({ field: controllerField }) => (
                    <FormControlLabel
                        control={
                            <Checkbox
                                {...controllerField}
                                checked={controllerField.value}
                                onChange={(e) => controllerField.onChange(e.target.checked)}
                                disabled={disabled}
                            />
                        }
                        label={t("products.ProductParameters.sideEast")}
                        sx={disabled ? disabledCheckboxSx : undefined}
                    />
                )}
            />
            <Controller
                name={fieldNames.west}
                control={control}
                render={({ field: controllerField }) => (
                    <FormControlLabel
                        control={
                            <Checkbox
                                {...controllerField}
                                checked={controllerField.value}
                                onChange={(e) => controllerField.onChange(e.target.checked)}
                                disabled={disabled}
                            />
                        }
                        label={t("products.ProductParameters.sideWest")}
                        sx={disabled ? disabledCheckboxSx : undefined}
                    />
                )}
            />
            <Controller
                name={fieldNames.north}
                control={control}
                render={({ field: controllerField }) => (
                    <FormControlLabel
                        control={
                            <Checkbox
                                {...controllerField}
                                checked={controllerField.value}
                                onChange={(e) => controllerField.onChange(e.target.checked)}
                                disabled={disabled}
                            />
                        }
                        label={t("products.ProductParameters.sideNorth")}
                        sx={disabled ? disabledCheckboxSx : undefined}
                    />
                )}
            />
            <Controller
                name={fieldNames.south}
                control={control}
                render={({ field: controllerField }) => (
                    <FormControlLabel
                        control={
                            <Checkbox
                                {...controllerField}
                                checked={controllerField.value}
                                onChange={(e) => controllerField.onChange(e.target.checked)}
                                disabled={disabled}
                            />
                        }
                        label={t("products.ProductParameters.sideSouth")}
                        sx={disabled ? disabledCheckboxSx : undefined}
                    />
                )}
            />
        </Box>
    );
};

const FootageColumn = (props) => {
    const { t } = useTranslation();
    const { control, forMultipleProducts, readOnly, fieldNames } = props;
    const { setValue } = useFormContext();
    const formLabelSx = { textAlign: "left", marginBottom: "0.5rem", fontWeight: "bold" };
    const formControlBoxSx = { display: "flex", flexDirection: "column", gap: 1 };

    const selectedTypeId = useWatch({
        control,
        name: fieldNames.type,
    });

    const { data: productTypesData } = useProductTypes();
    const productTypes = productTypesData || [];

    const selectedType = productTypes.find((type) => type.id === selectedTypeId);
    const isFootageDisabled = selectedType?.no_footage || false;

    useEffect(() => {
        if (isFootageDisabled) {
            setValue(fieldNames.footage, 1);
            setValue(fieldNames.footageFinal, 1);
        }
    }, [isFootageDisabled, setValue, fieldNames.footage, fieldNames.footageFinal]);

    const footageInputDisabled = forMultipleProducts || readOnly || isFootageDisabled;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: "200px" }}>
            <Box sx={formControlBoxSx}>
                <FormLabel htmlFor="footage" sx={formLabelSx}>
                    {t("products.ProductParameters.footage")}
                </FormLabel>
                <Controller
                    name={fieldNames.footage}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            id="footage"
                            type="number"
                            disabled={footageInputDisabled}
                            sx={footageInputDisabled ? visualDisabledInputSx : disabledInputSx}
                            inputProps={{
                                min: 0,
                                step: "any",
                            }}
                        />
                    )}
                />
            </Box>
            <Box sx={formControlBoxSx}>
                <FormLabel htmlFor="footageFinal" sx={formLabelSx}>
                    {t("products.ProductParameters.footageFinal")}
                </FormLabel>
                <Controller
                    name={fieldNames.footageFinal}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            id="footageFinal"
                            type="number"
                            disabled={footageInputDisabled}
                            sx={footageInputDisabled ? visualDisabledInputSx : disabledInputSx}
                            inputProps={{
                                min: 0,
                                step: "any",
                            }}
                        />
                    )}
                />
            </Box>
        </Box>
    );
};
